import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  VStack,
  useColorModeValue,
  Text,
  Spinner,
  Button,
  HStack,
  Image,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Tag,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { PrivateNavbar } from "./layout/PrivateNavbar";
import { Footer } from "./layout/Footer";
import { paymentsAPI, userAPI } from "../services";
import { FiCode, FiDollarSign, FiPlus } from "react-icons/fi";
import { Payment, User } from "types";
import { useAuthStore } from "../stores/authStore";
import { UsersTable } from "./admin/UsersTable";
import { PaymentsTable } from "./admin/PaymentsTable";

export const Admin: React.FC = () => {
  const [payments, setPayments] = useState<Payment[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [pageUsers, setPageUsers] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalPagesUsers, setTotalPagesUsers] = useState(1);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const bgColor = useColorModeValue("gray.50", "gray.900");
  const tableBg = useColorModeValue("white", "gray.800");
  const { i18n } = useTranslation();
  const { user } = useAuthStore();

  const loadPayments = async () => {
    try {
      const data = await paymentsAPI.getAllPayments(page, 10);
      console.log(data);
      setPayments(data.payments);
      setTotalPages(data.pagination.pages);
    } catch (err) {
      console.error("Error loading payments:", err);
      setError(t("payments.error.loading"));
    } finally {
      setIsLoading(false);
    }
  };
  const loadUsers = async () => {
    try {
      const data = await userAPI.findAll(pageUsers, 10);
      console.log(data);
      setUsers(data.users);
      setTotalPagesUsers(data.pagination.pages);
    } catch (err) {
      console.error("Error loading users:", err);
      setError(t("users.error.loading"));
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    if (!user?.isAdmin) {
      navigate("/");
    }
  }, []);

  useEffect(() => {
    loadPayments();
  }, [page]);

  useEffect(() => {
    loadUsers();
  }, [pageUsers]);

  useEffect(() => {
    loadPayments();
    loadUsers();
  }, [t]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(
      i18n.language === "pt-BR" ? "pt-BR" : "en-US"
    );
  };

  const formatAmount = (amount: number, currency: string) => {
    return new Intl.NumberFormat(i18n.language, {
      style: "currency",
      currency: currency,
    }).format(amount);
  };

  return (
    <Box minH="100vh" display="flex" flexDirection="column">
      <PrivateNavbar />

      <Box flex="1" bg={bgColor} py={20}>
        <Container maxW="container.xl">
          <VStack spacing={8} align="stretch">
            <Box
              display="flex"
              flexDirection={{ base: "column", md: "row" }}
              justifyContent={{ base: "center", md: "space-between" }}
              alignItems={{ base: "flex-start", md: "center" }}
              gap={4}
            >
              <Heading
                size="xl"
                fontFamily="Nasalization"
                bgGradient="linear(to-r, brand.primary, brand.secondary)"
                bgClip="text"
              >
                {t("users.admin.title")}
              </Heading>
            </Box>

            {isLoading ? (
              <Box textAlign="center" py={10}>
                <Spinner size="xl" color="brand.primary" />
              </Box>
            ) : error ? (
              <Box textAlign="center" py={10}>
                <Text color="red.500">{error}</Text>
              </Box>
            ) : users.length === 0 ? (
              <Box textAlign="center" py={10}>
                <Text>{t("users.history.empty")}</Text>
              </Box>
            ) : (
              <Box bg={tableBg} shadow="base" rounded="lg" overflow="auto">
                <UsersTable
                  users={users}
                  page={pageUsers}
                  totalPages={totalPagesUsers}
                  setPage={setPageUsers}
                />
              </Box>
            )}
          </VStack>
        </Container>
      </Box>

      <Box flex="1" bg={bgColor} py={20}>
        <Container maxW="container.xl">
          <VStack spacing={8} align="stretch">
            <Box
              display="flex"
              flexDirection={{ base: "column", md: "row" }}
              justifyContent={{ base: "center", md: "space-between" }}
              alignItems={{ base: "flex-start", md: "center" }}
              gap={4}
            >
              <Heading
                size="xl"
                fontFamily="Nasalization"
                bgGradient="linear(to-r, brand.primary, brand.secondary)"
                bgClip="text"
              >
                {t("payments.history.title")}
              </Heading>
            </Box>

            {isLoading ? (
              <Box textAlign="center" py={10}>
                <Spinner size="xl" color="brand.primary" />
              </Box>
            ) : error ? (
              <Box textAlign="center" py={10}>
                <Text color="red.500">{error}</Text>
              </Box>
            ) : users.length === 0 ? (
              <Box textAlign="center" py={10}>
                <Text>{t("users.history.empty")}</Text>
              </Box>
            ) : (
              <Box bg={tableBg} shadow="base" rounded="lg" overflow="auto">
                <PaymentsTable
                  payments={payments}
                  page={page}
                  totalPages={totalPages}
                  setPage={setPage}
                />
              </Box>
            )}
          </VStack>
        </Container>
      </Box>

      <Footer />
    </Box>
  );
};
